/**
 * Campaign Context - Global Campaign State Management
 *
 * File: Mythbound.App/src/context/CampaignContext.tsx
 * Created: 2026-01-06
 * Last Modified: 2026-01-06
 *
 * PURPOSE:
 * Centralize campaign state (current campaign, list, participants)
 * to avoid prop drilling through components. Shares state across
 * Dashboard, CampaignPage, SessionView.
 *
 * STATE:
 * - currentCampaign: Campaign | null
 * - campaigns: Campaign[]
 * - selectedCampaignId: string | null
 * - participants: Participant[]
 * - isLoading: boolean
 * - error: string | null
 *
 * ACTIONS:
 * - setCurrentCampaign(campaign)
 * - setCampaigns(campaigns)
 * - addCampaign(campaign)
 * - updateCampaign(campaign_id, updates)
 * - deleteCampaign(campaign_id)
 * - setParticipants(participants)
 * - addParticipant(participant)
 * - removeParticipant(user_id)
 *
 * USAGE:
 * const { currentCampaign, campaigns } = useCampaign();
 *
 * INTEGRATION:
 * - Load campaigns on app start (in App.tsx)
 * - Update on WebSocket campaign.* events
 * - Poll every 5 minutes for campaign list changes (optional)
 *
 * Pipeline: Components → CampaignContext → CampaignService → Backend
 * Dependencies: React, campaignService
 */

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { campaignService, type Campaign, type CampaignListItem, type Participant } from '../services/api/CampaignService';
// Added 2026-02-24: Import useAuth to gate campaign loading on authentication state.
// Prevents race condition where campaigns fire before auth completes (always 401).
import { useAuth } from './AuthContext';
import { ApiError, type ApiErrorKind } from '../services/api/ApiError';

interface CampaignContextType {
  // State
  currentCampaign: Campaign | null;
  campaigns: CampaignListItem[];
  participants: Participant[];
  isLoading: boolean;
  error: string | null;
  /** Loading/error for selected campaign details, independent of list refreshes. */
  isCampaignLoading: boolean;
  campaignError: string | null;
  campaignErrorKind: ApiErrorKind | null;
  participantsError: string | null;

  // Actions
  setCurrentCampaign: (campaign: Campaign | null) => void;
  setCampaigns: (campaigns: CampaignListItem[]) => void;
  addCampaign: (campaign: CampaignListItem) => void;
  updateCampaign: (campaignId: string, updates: Partial<Campaign>) => void;
  deleteCampaign: (campaignId: string) => void;
  addParticipant: (participant: Participant) => void;
  removeParticipant: (userId: string) => void;
  applyRealtimeCampaignProjection: (campaignId: string, projection: { campaign_name: string; participant_count: number }) => void;
  loadCampaigns: () => Promise<void>;
  loadCampaign: (campaignId: string) => Promise<void>;
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

export const CampaignProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentCampaign, setCurrentCampaign] = useState<Campaign | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCampaignLoading, setIsCampaignLoading] = useState(false);
  const [campaignError, setCampaignError] = useState<string | null>(null);
  const [campaignErrorKind, setCampaignErrorKind] = useState<ApiErrorKind | null>(null);
  const [participantsError, setParticipantsError] = useState<string | null>(null);
  // Separate generations keep list refreshes and selected detail reads independent.
  const campaignRequestGeneration = useRef(0);
  const campaignListRequestGeneration = useRef(0);

  // Added 2026-02-24: Gate campaign loading on authentication state.
  // CampaignProvider is nested inside AuthProvider in App.tsx, so useAuth() is safe.
  // Without this gate, loadCampaigns fires before auth completes → always 401.
  const { isAuthenticated, userInfo } = useAuth();

  // Fence a changed auth scope during render so child route effects cannot
  // start a request that a later parent effect immediately invalidates.
  const authScopeRef = useRef({ isAuthenticated, userId: userInfo?.id });
  if (authScopeRef.current.isAuthenticated !== isAuthenticated || authScopeRef.current.userId !== userInfo?.id) {
    authScopeRef.current = { isAuthenticated, userId: userInfo?.id };
    campaignRequestGeneration.current += 1;
    campaignListRequestGeneration.current += 1;
    setCurrentCampaign(null);
    setCampaigns([]);
    setParticipants([]);
    setIsLoading(false);
    setError(null);
    setIsCampaignLoading(false);
    setCampaignError(null);
    setCampaignErrorKind(null);
    setParticipantsError(null);
  }
  // Provider unmount is also a lifetime boundary for outstanding HTTP work.
  useEffect(() => () => {
    campaignRequestGeneration.current += 1;
    campaignListRequestGeneration.current += 1;
  }, []);

  /**
   * Load campaigns list from API
   *
   * Called on context mount and can be manually triggered
   * to refresh campaign list.
   */
  const loadCampaigns = useCallback(async () => {
    const requestId = ++campaignListRequestGeneration.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await campaignService.listCampaigns();
      if (requestId !== campaignListRequestGeneration.current) return;
      setCampaigns(result.campaigns);
    } catch (err) {
      if (requestId !== campaignListRequestGeneration.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load campaigns';
      setError(message);
      console.error('Load campaigns error:', message);
    } finally {
      if (requestId === campaignListRequestGeneration.current) setIsLoading(false);
    }
  }, []);

  // Auth/account changes request their own list. The stable callback is also
  // explicit so future callback inputs cannot leave this effect stale.
  useEffect(() => {
    if (isAuthenticated) void loadCampaigns();
  }, [isAuthenticated, userInfo?.id, loadCampaigns]);

  /**
   * Load single campaign with full details
   *
   * @param campaignId Campaign UUID
   */
  const loadCampaign = useCallback(async (campaignId: string) => {
    const requestId = ++campaignRequestGeneration.current;
    setIsCampaignLoading(true);
    setCampaignError(null);
    setCampaignErrorKind(null);
    setParticipantsError(null);
    // Any detail refresh starts from a blank selection so stale detail or
    // membership data cannot appear authoritative while the request is pending.
    setCurrentCampaign(null);
    setParticipants([]);
    try {
      const campaign = await campaignService.getCampaign(campaignId);
      if (requestId !== campaignRequestGeneration.current) return;
      setCurrentCampaign(campaign);

      // Load the authorized member projection separately from the campaign summary.
      try {
        const fetchedParticipants = await campaignService.getParticipants(campaignId);
        if (requestId !== campaignRequestGeneration.current) return;
        setParticipants(fetchedParticipants);
        setParticipantsError(null);
      } catch (participantError) {
        if (requestId !== campaignRequestGeneration.current) return;
        // Keep valid detail, but never describe an unavailable member list as empty.
        setParticipants([]);
        setParticipantsError(participantError instanceof Error ? participantError.message : 'Failed to load participants');
        console.warn('[CampaignContext] Failed to load participants:', participantError);
      }
    } catch (err) {
      if (requestId !== campaignRequestGeneration.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load campaign';
      setCampaignError(message);
      setCampaignErrorKind(err instanceof ApiError ? err.kind : 'unknown');
      setCurrentCampaign(null);
      setParticipants([]);
      setParticipantsError(null);
      console.error('Load campaign error:', message);
    } finally {
      if (requestId === campaignRequestGeneration.current) setIsCampaignLoading(false);
    }
  }, []);

  /**
   * Add new campaign to list
   *
   * Used after creating a new campaign to update local state.
   */
  const addCampaign = useCallback((campaign: CampaignListItem) => {
    setCampaigns(prev => [campaign, ...prev]);
  }, []);

  /**
   * Update campaign in list
   *
   * @param campaignId Campaign UUID
   * @param updates Partial campaign updates
   */
  const updateCampaign = useCallback((campaignId: string, updates: Partial<Campaign>) => {
    setCampaigns(prev =>
      prev.map(c => c.campaign_id === campaignId ? { ...c, ...updates } : c)
    );

    // Update current campaign if it matches
    if (currentCampaign?.campaign_id === campaignId) {
      setCurrentCampaign(prev => prev ? { ...prev, ...updates } : null);
    }
  }, [currentCampaign]);

  /**
   * Delete campaign from list
   *
   * @param campaignId Campaign UUID
   */
  const deleteCampaign = useCallback((campaignId: string) => {
    setCampaigns(prev => prev.filter(c => c.campaign_id !== campaignId));

    // Clear current campaign if it matches
    if (currentCampaign?.campaign_id === campaignId) {
      setCurrentCampaign(null);
    }
  }, [currentCampaign]);

  /**
   * Add participant to current campaign
   *
   * @param participant Participant object
   */
  const addParticipant = useCallback((participant: Participant) => {
    setParticipants(prev => [...prev, participant]);
  }, []);

  /**
   * Remove participant from current campaign
   *
   * @param userId User ID to remove
   */
  const removeParticipant = useCallback((userId: string) => {
    setParticipants(prev => prev.filter(p => p.user_id !== userId));
  }, []);

  /** Apply only campaign fields present in the privacy-safe realtime projection. */
  const applyRealtimeCampaignProjection = useCallback((campaignId: string, projection: { campaign_name: string; participant_count: number }) => {
    setCurrentCampaign(previous => previous?.campaign_id === campaignId ? { ...previous, ...projection } : previous);
    setCampaigns(previous => previous.map(campaign => campaign.campaign_id === campaignId ? { ...campaign, ...projection } : campaign));
  }, []);

  const value: CampaignContextType = {
    currentCampaign,
    campaigns,
    participants,
    isLoading,
    error,
    isCampaignLoading,
    campaignError,
    campaignErrorKind,
    participantsError,
    setCurrentCampaign,
    setCampaigns,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    addParticipant,
    removeParticipant,
    applyRealtimeCampaignProjection,
    loadCampaigns,
    loadCampaign
  };

  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
};

/**
 * Hook to use campaign context
 *
 * @returns CampaignContextType
 * @throws Error if used outside CampaignProvider
 */
export const useCampaign = (): CampaignContextType => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error('useCampaign must be used within CampaignProvider');
  }
  return context;
};

export const useCampaignContext = useCampaign;
